// The builder's constraint palette (mockup 07): groups of types, each with how it is drawn on
// the grid (its gesture) and the entry it makes (docs/CONSTRAINTS.md). The featured ones are
// the mockup's; "Show all" lists every type with a plugin.
import type { ConstraintEntry } from '@zinc/shared';

/** How a type is drawn:
 * - line: drag through cells (8 directions); arrow: the same, from the circle
 * - cage: drag over cells, one entry for them all
 * - cell: tap cells, one entry each (odd, even, minimum, maximum, fortress)
 * - edge: tap the edge between two cells
 * - outside: tap outside the grid, next to a row or column
 * - little-killer: tap outside the grid, the clue points diagonally in
 * - corner: tap where four cells meet (quadruple)
 * - square: tap the top-left cell of a 3×3 square (magic square)
 * - clone: drag one area, then its copy
 * - region: paint cells into a jigsaw region
 * - global: a rule for the whole grid, switched on or off */
export type Gesture =
  | 'line'
  | 'arrow'
  | 'cage'
  | 'cell'
  | 'edge'
  | 'outside'
  | 'little-killer'
  | 'corner'
  | 'square'
  | 'clone'
  | 'region'
  | 'global';

export type Group = 'lines' | 'cages' | 'edges' | 'cells';

export const GROUPS: { key: Group; name: string }[] = [
  { key: 'lines', name: 'Lines' },
  { key: 'cages', name: 'Cages and sums' },
  { key: 'edges', name: 'Dots and edges' },
  { key: 'cells', name: 'Cells, regions, global' },
];

/** A choice on a palette item that changes the entry it makes (white or black dot). */
export interface Variant {
  key: string;
  label: string;
  type: string;
  params?: Record<string, unknown>;
}

export interface PaletteItem {
  id: string;
  name: string;
  group: Group;
  gesture: Gesture;
  /** The entry's type and its default parameters (the first variant's, when there are some). */
  type: string;
  params?: Record<string, unknown>;
  variants?: Variant[];
  /** Words the search also matches. */
  keywords?: string;
  /** In the mockup's short list; the rest show under "Show all". */
  featured?: boolean;
  /** Plugin ids the app may add for this item (Semi-auto's "The app may add"); none: the app
   * can't place this type. */
  allow?: string[];
}

export const PALETTE: PaletteItem[] = [
  // Lines
  {
    id: 'thermo',
    name: 'Thermo',
    group: 'lines',
    gesture: 'line',
    type: 'thermo',
    featured: true,
    allow: ['thermo'],
    keywords: 'thermometer',
  },
  {
    id: 'arrow',
    name: 'Arrow',
    group: 'lines',
    gesture: 'arrow',
    type: 'arrow',
    featured: true,
    allow: ['arrow'],
  },
  {
    id: 'renban',
    name: 'Renban',
    group: 'lines',
    gesture: 'line',
    type: 'renban',
    featured: true,
    allow: ['renban'],
    keywords: 'consecutive',
  },
  {
    id: 'german-whispers',
    name: 'German whispers',
    group: 'lines',
    gesture: 'line',
    type: 'whispers',
    params: { difference: 5 },
    featured: true,
    allow: ['whispers'],
  },
  {
    id: 'dutch-whispers',
    name: 'Dutch whispers',
    group: 'lines',
    gesture: 'line',
    type: 'whispers',
    params: { difference: 4 },
    featured: true,
  },
  {
    id: 'palindrome',
    name: 'Palindrome',
    group: 'lines',
    gesture: 'line',
    type: 'palindrome',
    featured: true,
    allow: ['palindrome'],
  },
  {
    id: 'region-sum',
    name: 'Region sum',
    group: 'lines',
    gesture: 'line',
    type: 'region-sum',
    featured: true,
    allow: ['region-sum'],
  },
  {
    id: 'between',
    name: 'Between',
    group: 'lines',
    gesture: 'line',
    type: 'between',
    featured: true,
    allow: ['between'],
  },
  {
    id: 'slow-thermo',
    name: 'Slow thermo',
    group: 'lines',
    gesture: 'line',
    type: 'slow-thermo',
    allow: ['slow-thermo'],
  },
  {
    id: 'double-arrow',
    name: 'Double arrow',
    group: 'lines',
    gesture: 'line',
    type: 'double-arrow',
    allow: ['double-arrow'],
  },
  {
    id: 'nabner',
    name: 'Nabner',
    group: 'lines',
    gesture: 'line',
    type: 'nabner',
    allow: ['nabner'],
  },
  {
    id: 'whispers',
    name: 'Whispers (custom)',
    group: 'lines',
    gesture: 'line',
    type: 'whispers',
    params: { difference: 3 },
  },
  {
    id: 'lockout',
    name: 'Lockout',
    group: 'lines',
    gesture: 'line',
    type: 'lockout',
    params: { difference: 4 },
    allow: ['lockout'],
  },
  {
    id: 'entropic',
    name: 'Entropic',
    group: 'lines',
    gesture: 'line',
    type: 'entropic',
    allow: ['entropic'],
  },
  {
    id: 'modular',
    name: 'Modular',
    group: 'lines',
    gesture: 'line',
    type: 'modular',
    allow: ['modular'],
  },
  {
    id: 'parity',
    name: 'Parity line',
    group: 'lines',
    gesture: 'line',
    type: 'parity',
    allow: ['parity'],
  },
  {
    id: 'zipper',
    name: 'Zipper',
    group: 'lines',
    gesture: 'line',
    type: 'zipper',
    allow: ['zipper'],
  },
  // Cages and sums
  {
    id: 'killer',
    name: 'Killer',
    group: 'cages',
    gesture: 'cage',
    type: 'killer',
    featured: true,
    allow: ['killer'],
    keywords: 'cage sum',
  },
  {
    id: 'little-killer',
    name: 'Little killer',
    group: 'cages',
    gesture: 'little-killer',
    type: 'little-killer',
    params: { sum: 10 },
    featured: true,
    allow: ['little-killer'],
  },
  {
    id: 'sandwich',
    name: 'Sandwich',
    group: 'cages',
    gesture: 'outside',
    type: 'sandwich',
    params: { sum: 0 },
    featured: true,
    allow: ['sandwich'],
  },
  {
    id: 'x-sum',
    name: 'X-sums',
    group: 'cages',
    gesture: 'outside',
    type: 'x-sum',
    params: { sum: 10 },
    featured: true,
    allow: ['x-sum'],
  },
  {
    id: 'skyscraper',
    name: 'Skyscrapers',
    group: 'cages',
    gesture: 'outside',
    type: 'skyscraper',
    params: { count: 2 },
    allow: ['skyscraper'],
  },
  {
    id: 'frame-sum',
    name: 'Frame sums',
    group: 'cages',
    gesture: 'outside',
    type: 'frame-sum',
    params: { sum: 15 },
    allow: ['frame-sum'],
  },
  {
    id: 'battlefield',
    name: 'Battlefield',
    group: 'cages',
    gesture: 'outside',
    type: 'battlefield',
    params: { sum: 0 },
    allow: ['battlefield'],
  },
  // Dots and edges
  {
    id: 'kropki',
    name: 'Kropki',
    group: 'edges',
    gesture: 'edge',
    type: 'difference',
    params: { value: 1 },
    featured: true,
    allow: ['difference', 'ratio'],
    keywords: 'dot white black',
    variants: [
      { key: 'white', label: 'White', type: 'difference', params: { value: 1 } },
      { key: 'black', label: 'Black', type: 'ratio', params: { value: 2 } },
    ],
  },
  {
    id: 'xv',
    name: 'XV',
    group: 'edges',
    gesture: 'edge',
    type: 'edge-sum',
    params: { value: 10, label: 'X' },
    featured: true,
    allow: ['edge-sum'],
    variants: [
      { key: 'x', label: 'X', type: 'edge-sum', params: { value: 10, label: 'X' } },
      { key: 'v', label: 'V', type: 'edge-sum', params: { value: 5, label: 'V' } },
    ],
  },
  {
    id: 'greater-than',
    name: 'Greater than',
    group: 'edges',
    gesture: 'edge',
    type: 'greater-than',
    featured: true,
    allow: ['greater-than'],
    keywords: 'inequality less',
  },
  {
    id: 'quadruple',
    name: 'Quadruple',
    group: 'edges',
    gesture: 'corner',
    type: 'quadruple',
    params: { digits: [] },
    featured: true,
    allow: ['quadruple'],
  },
  {
    id: 'difference',
    name: 'Difference dot',
    group: 'edges',
    gesture: 'edge',
    type: 'difference',
    params: { value: 2 },
  },
  {
    id: 'ratio',
    name: 'Ratio dot',
    group: 'edges',
    gesture: 'edge',
    type: 'ratio',
    params: { value: 3 },
  },
  {
    id: 'edge-sum',
    name: 'Sum clue',
    group: 'edges',
    gesture: 'edge',
    type: 'edge-sum',
    params: { value: 7 },
  },
  // Cells, regions, global
  {
    id: 'odd-even',
    name: 'Odd / even',
    group: 'cells',
    gesture: 'cell',
    type: 'odd',
    featured: true,
    allow: ['odd', 'even'],
    keywords: 'parity circle square',
    variants: [
      { key: 'odd', label: 'Odd', type: 'odd' },
      { key: 'even', label: 'Even', type: 'even' },
    ],
  },
  {
    id: 'irregular',
    name: 'Irregular',
    group: 'cells',
    gesture: 'region',
    type: 'regions',
    featured: true,
    keywords: 'jigsaw region',
  },
  {
    id: 'anti-knight',
    name: 'Anti-knight',
    group: 'cells',
    gesture: 'global',
    type: 'anti-knight',
    featured: true,
  },
  {
    id: 'diagonal',
    name: 'Diagonal',
    group: 'cells',
    gesture: 'global',
    type: 'diagonal',
    featured: true,
    keywords: 'x-sudoku',
    variants: [
      { key: 'positive', label: '/', type: 'diagonal', params: { direction: 'positive' } },
      { key: 'negative', label: '\\', type: 'diagonal', params: { direction: 'negative' } },
    ],
  },
  { id: 'anti-king', name: 'Anti-king', group: 'cells', gesture: 'global', type: 'anti-king' },
  {
    id: 'non-consecutive',
    name: 'Non-consecutive',
    group: 'cells',
    gesture: 'global',
    type: 'non-consecutive',
  },
  {
    id: 'disjoint-groups',
    name: 'Disjoint groups',
    group: 'cells',
    gesture: 'global',
    type: 'disjoint-groups',
  },
  {
    id: 'extra-region',
    name: 'Extra region',
    group: 'cells',
    gesture: 'cage',
    type: 'extra-region',
    allow: ['extra-region'],
    keywords: 'windoku',
  },
  {
    id: 'min-max',
    name: 'Minimum / maximum',
    group: 'cells',
    gesture: 'cell',
    type: 'minimum',
    allow: ['minimum', 'maximum'],
    variants: [
      { key: 'minimum', label: 'Minimum', type: 'minimum' },
      { key: 'maximum', label: 'Maximum', type: 'maximum' },
    ],
  },
  {
    id: 'fortress',
    name: 'Fortress',
    group: 'cells',
    gesture: 'cell',
    type: 'fortress',
    allow: ['fortress'],
  },
  { id: 'clone', name: 'Clone', group: 'cells', gesture: 'clone', type: 'clone', allow: ['clone'] },
  {
    id: 'magic-square',
    name: 'Magic square',
    group: 'cells',
    gesture: 'square',
    type: 'magic-square',
  },
];

export function paletteItem(id: string): PaletteItem | undefined {
  return PALETTE.find((p) => p.id === id);
}

export function searchPalette(query: string, all: boolean): PaletteItem[] {
  const q = query.trim().toLowerCase();
  return PALETTE.filter((p) => {
    if (q) return `${p.name} ${p.keywords ?? ''} ${p.type}`.toLowerCase().includes(q);
    return all || p.featured;
  });
}

/** The entry type and parameters an item makes with a variant chosen. */
export function variantOf(item: PaletteItem, key: string | null): Variant {
  return (
    item.variants?.find((v) => v.key === key) ??
    item.variants?.[0] ?? { key: item.id, label: item.name, type: item.type, params: item.params }
  );
}

/** Choices for "The app may add": palette items the generator can place, plus givens. */
export const ADDABLE: { key: string; name: string; types: string[] }[] = [
  { key: 'given', name: 'Givens', types: ['given'] },
  ...PALETTE.filter((p) => p.allow).map((p) => ({ key: p.id, name: p.name, types: p.allow! })),
];

/** The chips for an allowed-types list (plugin ids): each addable item whose types are all in. */
export function allowedChips(allowed: string[]): { key: string; name: string; types: string[] }[] {
  return ADDABLE.filter((a) => a.types.every((t) => allowed.includes(t)));
}

/** Global rules the automatic mode passes to the generator for the whole grid. */
export const AUTO_GLOBALS = ['anti-knight', 'anti-king', 'non-consecutive'];

export function entryOf(
  type: string,
  params: Record<string, unknown> | undefined,
  rest: Record<string, unknown>,
): ConstraintEntry {
  return { type, ...(params ?? {}), ...rest };
}
